import { forwardRef, useImperativeHandle } from 'react';
import { EditIcon, PeopleSVG, PersonSVG } from 'src/assets/svg';
import { useUploadOrganizationAvatar } from 'src/view/hooks/useUploadOrganizationAvatar';
import { Organization } from 'src/domain/organization';
import CheckBox from './CheckBox';
import cx from 'classnames';

export type HandleUploadOrganizationAvatar = {
    uploadAvatar: (organizationOverride?: Organization) => Promise<void>;
};

type CheckboxInputProps = React.ComponentPropsWithoutRef<'input'> & {
    ref?: React.Ref<HTMLInputElement>;
};

type OrganizationAvatarInputProps = {
    organization?: Organization;
    readOnly?: boolean;
    logoAdjustmentInputProps?: CheckboxInputProps;
    logoAdjustmentLabel?: string;
    logoAdjustmentHelperText?: string;
    onAvatarDirtyChange?: (isDirty: boolean) => void;
    kind?: 'organization' | 'superuser';
};

const OrganizationAvatarInput = forwardRef<
    HandleUploadOrganizationAvatar,
    OrganizationAvatarInputProps
>(
    (
        {
            organization,
            readOnly = false,
            logoAdjustmentInputProps,
            logoAdjustmentLabel,
            logoAdjustmentHelperText,
            onAvatarDirtyChange,
            kind = 'organization',
        },
        ref,
    ) => {
        const { preview, file, onChange, uploadAvatar } =
            useUploadOrganizationAvatar(organization, kind);

        useImperativeHandle(ref, () => ({
            uploadAvatar: async (organizationOverride?: Organization) =>
                uploadAvatar(file ?? undefined, organizationOverride),
        }));

        const showLogoAdjustmentSetting =
            logoAdjustmentInputProps && logoAdjustmentLabel;
        const hasPreview = Boolean(preview) && !preview.startsWith('/static');
        const previewClassName = cx(
            'h-32 bg-vanillaWhite/20 border border-vanillaWhite/60 object-contain',
            kind === 'superuser'
                ? 'aspect-square rounded-full object-cover'
                : 'aspect-[5/2] border-md',
        );

        return (
            <div className="mb-6 flex flex-col items-center">
                <input
                    id="organization-avatar"
                    className="hidden"
                    type="file"
                    disabled={readOnly}
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={e => {
                        onChange(e);
                        onAvatarDirtyChange?.(Boolean(e.target.files?.length));
                    }}
                />

                <div className="grid w-full grid-cols-1 items-center gap-4 md:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)]">
                    <label
                        htmlFor={readOnly ? undefined : 'organization-avatar'}
                        className={cx(
                            'justify-self-center group relative transition-all md:col-start-2',
                            !readOnly && 'cursor-pointer hover:opacity-80',
                        )}
                    >
                        {hasPreview ? (
                            <img
                                src={preview}
                                alt=""
                                className={previewClassName}
                            />
                        ) : (
                            <div
                                className={cx(
                                    previewClassName,
                                    'flex items-center justify-center text-woodyBrown/40',
                                )}
                                aria-hidden="true"
                            >
                                {kind === 'superuser' ? (
                                    <PersonSVG className="h-10 w-10" />
                                ) : (
                                    <PeopleSVG className="h-10 w-10" />
                                )}
                            </div>
                        )}

                        {!readOnly ? (
                            <EditIcon className="w-8 h-8 absolute -bottom-2 -right-2 bg-lightPeach text-woodyBrown border border-vanillaWhite group-hover:bg-woodyBrown group-hover:text-lightPeach transition-all rounded-full p-1" />
                        ) : null}
                    </label>

                    {showLogoAdjustmentSetting ? (
                        <CheckBox
                            className="max-w-sm justify-self-center md:col-start-3 md:w-full md:max-w-xs md:justify-self-start"
                            label={logoAdjustmentLabel}
                            helperText={logoAdjustmentHelperText}
                            {...logoAdjustmentInputProps}
                            disabled={
                                readOnly || logoAdjustmentInputProps?.disabled
                            }
                        />
                    ) : null}
                </div>
            </div>
        );
    },
);

OrganizationAvatarInput.displayName = 'OrganizationAvatarInput';

export default OrganizationAvatarInput;
